import { type EventListLayoutType } from '@solinkcorp/authentication';
import { type EventStandardDefs } from '@solinkcorp/event-standard';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { Flex, MDIIcon } from '@solinkcorp/solink-components';
import AlarmWarning from '@solinkcorp/solink-components/svg/alarm-warning.svg?react';
import classNames from 'classnames';
import { type IEvent } from 'clients/PlatformApi';
import { CommonContext } from 'components/Contexts';
import { type PlayEventHandler } from 'components/EventPlayer/types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { PureComponent, useContext } from 'react';
import CenteredLoadingSpinner from 'src/core/components/loadingIndicator/CenteredLoadingSpinner';
import useLocale from 'src/hooks/useLocale';
import { useEventStandard } from 'utils/EventStandardCache/eventStandardCache';
import * as EventUtil from 'utils/EventUtil';
import { type CamerasDictionaryCamera } from 'utils/types';
import { type IEventDetailsItem } from '../VideoPlayer/components/PlayerOverlay/PlayerOverlay';
import { getFieldRenderer } from './renderers/fieldRenderers';
import { getFieldsToRender } from './renderers/getFieldsToRender';
import { PrintFieldRow } from './renderers/printRows';
import { type FieldRendererField, type FieldValue } from './renderers/types';

export { getFieldsToRender } from './renderers/getFieldsToRender';

const _void = () => undefined;
export const DETAILS_PADDING = 5;

const RECEIPT_FIELDS = new Set(['details.Receipt', 'queue', 'transaction']);

function selectFields(
  fields: FieldRendererField[],
  fieldSet: EventDetailsProps['fieldSet'],
): FieldRendererField[] {
  if (fieldSet === 'receipt') {
    return fields.filter((field) => RECEIPT_FIELDS.has(field.name));
  }
  if (fieldSet === 'details') {
    return fields.filter((field) => !RECEIPT_FIELDS.has(field.name));
  }
  return fields;
}

export interface EventDetailsProps {
  event: IEvent;
  searchString?: string;
  searchStringRegex?: RegExp;
  searchCapsules?: readonly string[];
  onContextMenu?: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    detailsField?: { name: string; value: FieldValue; i18n: string },
  ) => void;
  onEventRowClick?: PlayEventHandler;
  fontSize?: string;
  timezone?: string;
  activeItem?: IEventDetailsItem | undefined;
  playerTime?: number;
  isVideoPlaying?: boolean;
  layoutType?: EventListLayoutType;
  colorMode?: 'light' | 'dark';
  /**
   * Print mode. Labels sit in a fixed column, the receipt is split from the
   * other fields, and agent outputs render as plain text.
   */
  variant?: 'print';
  /** `receipt` renders the receipt only. `details` renders every other field. */
  fieldSet?: 'receipt' | 'details';
}

export interface EventDetailsInnerProps extends EventDetailsProps {
  camerasDictionary: { [cameraId: string]: CamerasDictionaryCamera };
  locale: LocaleDefinition;
  eventStandard: EventStandardDefs;
}

class EventDetails extends PureComponent<EventDetailsInnerProps> {
  protected node: HTMLDivElement | undefined | null;

  render() {
    const { eventStandard, event, locale, colorMode, variant, fieldSet } = this.props;
    const fields = selectFields(getFieldsToRender(eventStandard, event), fieldSet);
    const isAccessRequestEvent = Boolean(event.details?.isAccessRequestEvent);
    const stateName = EventUtil.i18nEventFieldName('state', false, eventStandard);
    const alarmStateLabel =
      stateName === 'state' ? EventUtil.getFriendlyFieldName('state') : stateName;

    if (!fields.length) {
      return null;
    }

    const isOpenAlarm =
      EventUtil.isAlarm(event) &&
      EventUtil.isOpen(event) &&
      !isAccessRequestEvent &&
      fieldSet !== 'receipt';
    const fieldsContent = fields.map((f) => this._renderField(f));

    if (variant === 'print') {
      return (
        <>
          {isOpenAlarm && (
            <PrintFieldRow label={alarmStateLabel} value={locale.alarms.activeAlarm} />
          )}
          {fieldsContent}
        </>
      );
    }

    return (
      <div
        className={classNames('receipt-detail', { [colorMode ?? '']: !!colorMode })}
        ref={(node) => {
          this.node = node;
        }}
        style={{ padding: `${DETAILS_PADDING}px 0` }}
      >
        {isOpenAlarm && (
          <Flex alignItems="center" className="open-alarm-tag">
            <MDIIcon icon={AlarmWarning} />
            <span>{locale.alarms.activeAlarm}</span>
          </Flex>
        )}
        {fieldsContent}
      </div>
    );
  }

  _renderField = (field: FieldRendererField) => {
    const {
      eventStandard,
      camerasDictionary,
      event,
      fontSize,
      locale,
      onContextMenu,
      onEventRowClick,
      timezone,
      activeItem,
      playerTime,
      isVideoPlaying,
      layoutType,
      variant,
    } = this.props;

    const { name, type } = field;

    const Renderer = getFieldRenderer(name, type);
    const fieldValue = get(event, name, undefined);

    if (Renderer && fieldValue !== undefined) {
      return (
        <Renderer
          eventStandard={eventStandard}
          camerasDictionary={camerasDictionary}
          event={event}
          eventId={event.id}
          fieldMetadata={field}
          fieldName={name}
          fieldValue={fieldValue}
          fontSize={fontSize}
          key={name}
          locale={locale}
          onContextMenu={onContextMenu || _void}
          onEventRowClick={onEventRowClick || _void}
          timezone={timezone}
          activeItem={activeItem}
          playerTime={playerTime}
          isVideoPlaying={isVideoPlaying}
          layoutType={layoutType}
          variant={variant}
        />
      );
    }
    return null;
  };

  // highlight search after render by adding the `highlight` class to the innermost `div` which text content matches search terms
  // not performed during the render step because to limit renderer's complexity and because
  //  some renderers may render html from a string template.
  componentDidMount() {
    const { searchCapsules, searchString, searchStringRegex } = this.props;

    if (!searchString && !searchStringRegex && !searchCapsules) {
      return;
    }

    this._dftHighlight(this.node);
  }

  // perform depth first traversale of the DOM starting at the given node, in search for a node
  // that contains the search term (or field name attribute reference for capsule-based search)
  _dftHighlight(div: HTMLElement | undefined | null) {
    const { searchCapsules, searchString, searchStringRegex } = this.props;

    if (!div) {
      return false;
    }

    // depth first search into the children, if there is any
    if (div.children && div.children.length > 0) {
      let match = false;
      for (const childDiv of div.children) {
        if (this._dftHighlight(childDiv as HTMLElement)) {
          match = true;
        }
      }
      if (match) {
        return true;
      }
    }

    // if no match is found in the children, or if no children existed, inspect the current node
    const searchStringMatch =
      searchString && div.innerText && div.innerText.indexOf(searchString) >= 0;
    const searchStringRegexMatch =
      searchStringRegex && div.innerText && div.innerText.match(searchStringRegex);

    const dataCapsuleName = div.attributes.getNamedItem('data-capsule-name')?.value;

    const searchCapsuleMatch =
      searchCapsules &&
      div.attributes &&
      dataCapsuleName &&
      searchCapsules.some((s) => s.split('&nbsp;')[0] === dataCapsuleName);

    if (searchStringMatch || searchStringRegexMatch || searchCapsuleMatch) {
      div.className += ' highlight';
      return true;
    }
    return false;
  }
}

export default function ComposedEventDetails(props: EventDetailsProps) {
  const locale = useLocale();
  const { camerasDictionary } = useContext(CommonContext);
  const eventStandard = useEventStandard();

  if (!eventStandard) {
    return <CenteredLoadingSpinner />;
  }

  return (
    <EventDetails
      camerasDictionary={isEmpty(camerasDictionary) ? {} : camerasDictionary}
      locale={locale}
      eventStandard={eventStandard}
      {...props}
    />
  );
}
